import i18n from '@risksmart-app/i18n/src/i18n';
import type { Action_Status_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import BadgeList from 'src/components/badge-list';
import type { LabelledIdArray } from 'src/rbac/types';

import { isActionStatusOverdue } from '@/components/action-status-badge/utils';
import type { StatefulTableOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import { useGetStatelessTableProps } from '@/utils/table/hooks/useGetStatelessTableProps';
import type {
  FieldConfig,
  TableFields,
  TablePropsWithActions,
  TableRecord,
} from '@/utils/table/types';

/**
 * Creates table props for relationship data sources using useGetStatelessTableProps.
 */
export const useRelationshipTableProps = <TRow extends TableRecord>(
  data: TRow[] | undefined,
  fields: TableFields<TRow>,
  entityLabel: string,
  options: StatefulTableOptions<TRow>
): TablePropsWithActions<TRow> => {
  return useGetStatelessTableProps<TRow>({
    data,
    fields,
    entityLabel,
    customAttributeFormIds: [],
    enableFiltering: false,
    ...options,
  });
};

/**
 * Creates a simple field config for a string field.
 */
export const simpleField = <T extends TableRecord>(
  header: string
): FieldConfig<T> => ({
  header,
});

/**
 * Field config for a `LabelledIdArray` column (e.g. action owners). Renders
 * labels as badges — without a custom `cell`, the table renders the raw
 * `{ label, id }` objects as React children and throws React error #31, which
 * the widget error boundary catches as "Widget content failed to load".
 */
export const ownersField = <T extends TableRecord>(
  header: string,
  accessor: (item: T) => LabelledIdArray
): FieldConfig<T> => ({
  header,
  cell: (item) => <BadgeList badges={accessor(item).map((o) => o.label)} />,
  sortingComparator: (a, b) =>
    accessor(a)
      .map((o) => o.label)
      .join(',')
      .localeCompare(
        accessor(b)
          .map((o) => o.label)
          .join(',')
      ),
  exportVal: (item) =>
    accessor(item)
      .map((o) => o.label)
      .join(','),
});

/**
 * Returns the localized "Overdue" / "On track" label for an action row.
 * Mirrors `isActionStatusOverdue` used by the single-entity action source, but
 * exposes overdue as an independent category so relationship widgets can chart
 * Status and Overdue separately (AC 2).
 */
export const getActionOverdueLabel = (
  action:
    | { DateDue?: null | string; Status?: Action_Status_Enum | null }
    | undefined
): string => {
  const isOverdue =
    action?.Status && action.DateDue
      ? isActionStatusOverdue({
          item: {
            Status: action.Status,
            DateDue: action.DateDue,
          },
        })
      : false;

  return isOverdue
    ? i18n.t('actions.columns.overdue')
    : i18n.t('actions.columns.on_track');
};
